/**
 * AEOReadinessButton.tsx
 *
 * On-demand button for checking AEO readiness.
 * - Initially shows "Is it ready for AEO?" button
 * - First click: computes and shows the score
 * - Second click: opens full Context Readiness Analysis panel
 *
 * Created: 2025-12-01
 */

import React, { useState } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Sparkles,
  HelpCircle,
  BarChart3
} from 'lucide-react';
import { ContextReadinessPanel } from './ContextReadinessPanel';

interface Props {
  siteId: string;
  categoryPath: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

interface ReadinessData {
  score: number;
  status: 'ready' | 'mostly_ready' | 'needs_work' | 'not_ready';
  aeoReady: boolean;
  answersReady: boolean;
  questionsReady: boolean;
}

const statusConfig = {
  ready: {
    color: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    icon: CheckCircle2,
    label: 'Ready for AEO'
  },
  mostly_ready: {
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300',
    icon: AlertCircle,
    label: 'Mostly Ready'
  },
  needs_work: {
    color: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300',
    icon: AlertTriangle,
    label: 'Needs Work'
  },
  not_ready: {
    color: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
    icon: XCircle,
    label: 'Not Ready'
  }
};

export const AEOReadinessButton: React.FC<Props> = ({
  siteId,
  categoryPath,
  variant = 'outline',
  size = 'sm',
  className = ''
}) => {
  const [state, setState] = useState<'idle' | 'loading' | 'loaded'>('idle');
  const [data, setData] = useState<ReadinessData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkReadiness = async () => {
    if (state === 'loaded' && data) {
      // Second click - open the dialog
      setDialogOpen(true);
      return;
    }

    // First click - load the data
    setState('loading');
    setError(null);

    try {
      const encodedCategory = encodeURIComponent(categoryPath);
      const response = await authFetch(
        `/api/context-readiness/category/${encodedCategory}?siteId=${siteId}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData({
            score: result.data.overallScore,
            status: result.data.status,
            aeoReady: result.data.contentReadiness.aeoPages.ready,
            answersReady: result.data.contentReadiness.answers.ready,
            questionsReady: result.data.contentReadiness.questions.ready
          });
          setState('loaded');
        } else {
          setError('Failed to analyze');
          setState('idle');
        }
      } else {
        setError('Failed to analyze');
        setState('idle');
      }
    } catch (err) {
      console.error('Failed to load context readiness:', err);
      setError('Analysis failed');
      setState('idle');
    }
  };

  // Idle state - show the question button
  if (state === 'idle') {
    return (
      <Button
        variant={variant}
        size={size}
        onClick={checkReadiness}
        className={`${className}`}
      >
        <HelpCircle className="h-4 w-4 mr-1.5" />
        Is it ready for AEO?
      </Button>
    );
  }

  // Loading state
  if (state === 'loading') {
    return (
      <Button
        variant={variant}
        size={size}
        disabled
        className={`${className}`}
      >
        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
        Analyzing...
      </Button>
    );
  }

  // Loaded state - show the score badge that can be clicked for details
  if (state === 'loaded' && data) {
    const config = statusConfig[data.status];
    const Icon = config.icon;

    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className={`${config.color} cursor-pointer hover:opacity-80 transition-opacity px-2.5 py-1 text-sm ${className}`}
                onClick={() => setDialogOpen(true)}
              >
                <Icon className="h-4 w-4 mr-1.5" />
                {data.score}%
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <div className="space-y-1.5 text-xs">
                <div className="font-semibold">{config.label} ({data.score}/100)</div>
                <div className="flex items-center gap-1.5">
                  {data.questionsReady ? (
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                  ) : (
                    <XCircle className="h-3 w-3 text-gray-400" />
                  )}
                  <span>Questions Ready</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {data.answersReady ? (
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                  ) : (
                    <XCircle className="h-3 w-3 text-gray-400" />
                  )}
                  <span>Answers Ready</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {data.aeoReady ? (
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                  ) : (
                    <XCircle className="h-3 w-3 text-gray-400" />
                  )}
                  <span>AEO Pages Ready</span>
                </div>
                <div className="text-muted-foreground pt-1 border-t mt-1">
                  Click for full analysis
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Context Readiness Analysis
              </DialogTitle>
            </DialogHeader>
            <ContextReadinessPanel
              siteId={siteId}
              categoryPath={categoryPath}
              onClose={() => setDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return null;
};

export default AEOReadinessButton;
